/**
 * Phase 8.5.1–8.5.3 — Scenario proposal evaluation contracts.
 *
 * Quality-owned. Deterministic evaluator scores AI (or heuristic)
 * Scenario proposals before human accept. Never creates Flows, never
 * approves Scenarios, never publishes.
 */

import { z } from 'zod';
import {
  AI_DESIGN_MODE,
  GeneratedScenarioDraftSchema,
  GeneratedScenarioStepSchema,
  ScenarioAcceptanceEvaluationSnapshotSchema,
  type ScenarioAcceptanceEvaluationSnapshot,
} from './scenarios';

export type { ScenarioAcceptanceEvaluationSnapshot };
export { ScenarioAcceptanceEvaluationSnapshotSchema };

export const SCENARIO_EVAL_SEVERITY = ['info', 'warn', 'block'] as const;
export type ScenarioEvalSeverity = (typeof SCENARIO_EVAL_SEVERITY)[number];

export const ScenarioEvalFindingSchema = z.object({
  code: z.string().trim().min(1).max(80),
  severity: z.enum(SCENARIO_EVAL_SEVERITY),
  message: z.string().trim().min(1).max(500),
  /** Zero-based step index within the evaluated step list (draft-flattened or steps-only). */
  stepIndex: z.number().int().min(0).optional(),
  /** Draft index when the finding applies to a specific draft. */
  draftIndex: z.number().int().min(0).optional(),
  requirementKey: z.string().trim().min(1).max(80).optional(),
  /** Phase 8.5.3 — what was checked (deterministic evidence). */
  checked: z.string().max(300).optional(),
  /** Phase 8.5.3 — source / reference used. */
  source: z.string().max(300).optional(),
  /** Phase 8.5.3 — why covered / weak / unsupported. */
  reason: z.string().max(500).optional(),
});
export type ScenarioEvalFinding = z.infer<typeof ScenarioEvalFindingSchema>;

/**
 * All scores are in [0, 1]. They are computed deterministically —
 * never invented from LLM self-confidence.
 */
export const ScenarioEvalScoresSchema = z.object({
  structureScore: z.number().min(0).max(1),
  traceabilityScore: z.number().min(0).max(1),
  /**
   * Phase 8.5.3 — AC / invention grounding quality.
   * Present on all new results; mirrors detailed grounding eval.
   */
  groundingScore: z.number().min(0).max(1),
  variantScore: z.number().min(0).max(1),
  hygieneScore: z.number().min(0).max(1),
  /** Higher = more duplicate risk (inverted vs other scores). */
  duplicateRisk: z.number().min(0).max(1),
  overall: z.number().min(0).max(1),
});
export type ScenarioEvalScores = z.infer<typeof ScenarioEvalScoresSchema>;

export const SCENARIO_EVAL_TESTABILITY = ['manual', 'partial', 'automatable', 'unknown'] as const;
export type ScenarioEvalTestability = (typeof SCENARIO_EVAL_TESTABILITY)[number];

export const ScenarioEvalTestabilityHintSchema = z.object({
  suitability: z.enum(SCENARIO_EVAL_TESTABILITY),
  /** Convert-readiness hint only — never triggers Flow creation. */
  convertReadyHint: z.boolean(),
  summary: z.string().max(300),
});
export type ScenarioEvalTestabilityHint = z.infer<typeof ScenarioEvalTestabilityHintSchema>;

export const SCENARIO_EVAL_EVIDENCE_KIND = [
  'requirement_ref',
  'ac_coverage',
  'unsupported_claim',
  'expected_result',
  'generic_content',
] as const;

export const SCENARIO_EVAL_EVIDENCE_STATUS = ['covered', 'partial', 'unsupported', 'missing', 'unavailable'] as const;

export const ScenarioEvalEvidenceSchema = z.object({
  kind: z.enum(SCENARIO_EVAL_EVIDENCE_KIND),
  status: z.enum(SCENARIO_EVAL_EVIDENCE_STATUS),
  checked: z.string().trim().min(1).max(300),
  source: z.string().trim().min(1).max(300),
  reason: z.string().trim().min(1).max(500),
  requirementKey: z.string().trim().min(1).max(80).optional(),
  /** Stable id such as AC-1 when acceptance criteria were split. */
  acceptanceCriterionId: z.string().trim().min(1).max(40).optional(),
  draftIndex: z.number().int().min(0).optional(),
  stepIndex: z.number().int().min(0).optional(),
});
export type ScenarioEvalEvidence = z.infer<typeof ScenarioEvalEvidenceSchema>;

/**
 * Phase 8.5.3 — grounding summary. Optional on older consumers; always
 * populated by evaluateScenarioProposal from 8.5.3 onward.
 */
export const ScenarioEvalGroundingSchema = z.object({
  /** True when at least one valid catalog requirement is referenced or AC overlap is strong. */
  requirementCovered: z.boolean(),
  acceptanceCriteriaCovered: z.number().int().min(0),
  acceptanceCriteriaTotal: z.number().int().min(0),
  unsupportedClaims: z.number().int().min(0),
  evidence: z.array(ScenarioEvalEvidenceSchema).max(100),
});
export type ScenarioEvalGrounding = z.infer<typeof ScenarioEvalGroundingSchema>;

export const ScenarioEvalResultSchema = z.object({
  scores: ScenarioEvalScoresSchema,
  findings: z.array(ScenarioEvalFindingSchema).max(100),
  testabilityHint: ScenarioEvalTestabilityHintSchema,
  /** Highest severity among findings; `info` when none. */
  highestSeverity: z.enum(SCENARIO_EVAL_SEVERITY),
  /** Phase 8.5.3 — requirement / AC grounding metadata. */
  grounding: ScenarioEvalGroundingSchema,
});
export type ScenarioEvalResult = z.infer<typeof ScenarioEvalResultSchema>;

/**
 * Phase 8.5.4 — flattened evaluation attached to Scenario AI generate responses.
 * Advisory only — never approves Scenarios or creates Flows.
 */
export const ScenarioProposalEvaluationSchema = z.object({
  overall: z.number().min(0).max(1),
  structureScore: z.number().min(0).max(1),
  variantScore: z.number().min(0).max(1),
  traceabilityScore: z.number().min(0).max(1),
  groundingScore: z.number().min(0).max(1),
  hygieneScore: z.number().min(0).max(1),
  findings: z.array(ScenarioEvalFindingSchema).max(100),
  grounding: ScenarioEvalGroundingSchema,
  testabilityHint: ScenarioEvalTestabilityHintSchema,
  /** Convenience mirror of testabilityHint.convertReadyHint. */
  convertReadyHint: z.boolean(),
  highestSeverity: z.enum(SCENARIO_EVAL_SEVERITY),
});
export type ScenarioProposalEvaluation = z.infer<typeof ScenarioProposalEvaluationSchema>;

/** Map full eval result → generate-response evaluation payload. */
export function toScenarioProposalEvaluation(result: ScenarioEvalResult): ScenarioProposalEvaluation {
  return ScenarioProposalEvaluationSchema.parse({
    overall: result.scores.overall,
    structureScore: result.scores.structureScore,
    variantScore: result.scores.variantScore,
    traceabilityScore: result.scores.traceabilityScore,
    groundingScore: result.scores.groundingScore,
    hygieneScore: result.scores.hygieneScore,
    findings: result.findings,
    grounding: result.grounding,
    testabilityHint: result.testabilityHint,
    convertReadyHint: result.testabilityHint.convertReadyHint,
    highestSeverity: result.highestSeverity,
  });
}

/**
 * Phase 8.5.5 — evaluator version stamped on persisted acceptance snapshots.
 * Bump when scoring/rules change so historical rows remain attributable.
 */
export const SCENARIO_EVALUATOR_VERSION = '8.5.5';

/** Build a persistable snapshot from a live proposal evaluation. */
export function toAcceptanceEvaluationSnapshot(
  evaluation: ScenarioProposalEvaluation,
  opts?: { acceptedAt?: string; evaluatorVersion?: string },
): ScenarioAcceptanceEvaluationSnapshot {
  let info = 0;
  let warn = 0;
  let block = 0;
  const codes: string[] = [];
  const seen = new Set<string>();
  for (const f of evaluation.findings) {
    if (f.severity === 'info') info += 1;
    else if (f.severity === 'warn') warn += 1;
    else block += 1;
    if (!seen.has(f.code) && codes.length < 30) {
      seen.add(f.code);
      codes.push(f.code);
    }
  }
  return ScenarioAcceptanceEvaluationSnapshotSchema.parse({
    evaluatorVersion: opts?.evaluatorVersion ?? SCENARIO_EVALUATOR_VERSION,
    overall: evaluation.overall,
    structureScore: evaluation.structureScore,
    variantScore: evaluation.variantScore,
    traceabilityScore: evaluation.traceabilityScore,
    groundingScore: evaluation.groundingScore,
    hygieneScore: evaluation.hygieneScore,
    findingsSummary: {
      highestSeverity: evaluation.highestSeverity,
      info,
      warn,
      block,
      codes,
    },
    groundingSummary: {
      requirementCovered: evaluation.grounding.requirementCovered,
      acceptanceCriteriaCovered: evaluation.grounding.acceptanceCriteriaCovered,
      acceptanceCriteriaTotal: evaluation.grounding.acceptanceCriteriaTotal,
      unsupportedClaims: evaluation.grounding.unsupportedClaims,
    },
    convertReadyHint: evaluation.convertReadyHint,
    acceptedAt: opts?.acceptedAt ?? new Date().toISOString(),
  });
}

export const ScenarioEvalRequirementContextSchema = z.object({
  id: z.string().uuid().optional(),
  key: z.string().trim().min(1).max(80),
  title: z.string().max(200).optional(),
  acceptanceCriteria: z.string().max(8_000).optional(),
});
export type ScenarioEvalRequirementContext = z.infer<typeof ScenarioEvalRequirementContextSchema>;

/**
 * Input to `evaluateScenarioProposal`. Accepts drafts and/or bare steps
 * plus optional catalogs for allowlist-style checks.
 */
export const ScenarioEvalInputSchema = z.object({
  drafts: z.array(GeneratedScenarioDraftSchema).max(20).optional(),
  steps: z.array(GeneratedScenarioStepSchema).max(40).optional(),
  mode: z.enum(AI_DESIGN_MODE).optional(),
  requirements: z.array(ScenarioEvalRequirementContextSchema).max(50).optional(),
  /** Free-form AC when structured requirements are unavailable (e.g. Story paste). */
  acceptanceCriteria: z.string().max(8_000).optional(),
  /** Optional free-form requirement / story body for grounding (not a catalog key). */
  requirementText: z.string().max(16_000).optional(),
  /** Existing step-component keys in the project. */
  componentCatalog: z.array(z.string().trim().min(1).max(80)).max(100).optional(),
  /** Dataset names allowed in ${Dataset.column} tokens. */
  testDataCatalog: z.array(z.string().trim().min(1).max(80)).max(100).optional(),
  /** Existing scenario titles for duplicate hints (title normalize only). */
  existingScenarioTitles: z.array(z.string().trim().min(1).max(200)).max(200).optional(),
});
export type ScenarioEvalInput = z.infer<typeof ScenarioEvalInputSchema>;
